import React, { useRef, useEffect, useCallback, useState } from 'react';
import './BottomSheet.css';

const BottomSheet = ({ 
  children,
  isOpen, // 외부에서 제어되는 열림 상태
  onClose, // 닫기 콜백
  snapPoints = [0.15, 0.4, 0.8], // (화면 높이 대비) 최소, 중간, 최대 높이
  initialSnap = 1, // 초기 스냅 인덱스 (중간 높이로 시작)
  header,
  backgroundColor = '#232323',
  showDragHandle = true,
}) => {
  const sheetRef = useRef(null);
  const contentRef = useRef(null); // 내부 콘텐츠의 ref를 추가합니다.
  const availableHeight = window.innerHeight * 0.92; // Navbar를 제외한 사용 가능한 높이

  const [currentHeight, setCurrentHeight] = useState(availableHeight * snapPoints[initialSnap]);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);

  // 디버깅 로그 (필요시에만)
  // console.log('BottomSheet 렌더링:', { 
  //   isOpen, 
  //   currentHeight, 
  //   snapPoints, 
  //   initialSnap,
  //   windowHeight: window.innerHeight 
  // });

  const updateSheetHeight = useCallback((height) => {
    if (sheetRef.current) {
      sheetRef.current.style.height = `${height}px`;
      setCurrentHeight(height);

    }
  }, []);

  const snapToPoint = useCallback((targetHeight) => {
    updateSheetHeight(targetHeight);
  }, [updateSheetHeight]);

  useEffect(() => {
    if (isOpen) {
      const initialHeight = availableHeight * snapPoints[initialSnap];

      snapToPoint(initialHeight);
      if (sheetRef.current) {
        sheetRef.current.style.height = `${initialHeight}px`;
      }
    }
    // eslint-disable-next-line
  }, [isOpen, snapPoints, initialSnap, snapToPoint, availableHeight]);

  const handleDragStart = useCallback((e) => {
    // 내부 콘텐츠가 스크롤된 상태에서 아래로 드래그할 때는 바텀시트 이동을 막습니다.
    if (contentRef.current && contentRef.current.scrollTop > 0) {
      // clientY가 startY보다 작다는 것은 위로 드래그(스크롤)한다는 의미이므로 허용합니다.
      if (e.type === 'touchstart' && e.touches[0].clientY < startY) {
         return; // 스크롤을 허용하고 드래그 시작 안함
      }
      if (e.type === 'mousedown' && e.clientY < startY) {
        return; // 스크롤을 허용하고 드래그 시작 안함
      }
    }
  
    if (!sheetRef.current) return;
    setIsDragging(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setStartY(clientY);
    setStartHeight(sheetRef.current.offsetHeight);
    sheetRef.current.style.transition = 'none';

  }, [startY]); // startY 의존성 추가

  const handleTouchStart = useCallback((e) => {
    handleDragStart(e);
  }, [handleDragStart]);

  const handleMouseDown = useCallback((e) => {
    handleDragStart(e);
  }, [handleDragStart]);

  const handleDragMove = useCallback((clientY) => {
    if (!isDragging || !sheetRef.current) return;
    const deltaY = clientY - startY;
    let newHeight = startHeight - deltaY;
    
    // 최소/최대 높이 제한을 다시 추가하여 고정 효과 구현
    const minAllowedHeight = availableHeight * snapPoints[0]; // 0.18 (최소)
    const maxAllowedHeight = availableHeight * snapPoints[snapPoints.length - 1]; // 0.85 (최대)
    newHeight = Math.max(minAllowedHeight, Math.min(newHeight, maxAllowedHeight));
    

    updateSheetHeight(newHeight);
  }, [isDragging, startY, startHeight, updateSheetHeight, currentHeight, availableHeight, snapPoints]);

  const handleTouchMove = useCallback((e) => {
    handleDragMove(e.touches[0].clientY);
  }, [handleDragMove]);

  const handleMouseMove = useCallback((e) => {
    handleDragMove(e.clientY);
  }, [handleDragMove]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging || !sheetRef.current) return;
    setIsDragging(false);
    sheetRef.current.style.transition = 'height 0.3s ease-out';

    const currentRelativeHeight = currentHeight / availableHeight;
    const startRelativeHeight = startHeight / availableHeight;

    // 시작점과 가장 가까운 스냅 인덱스를 찾습니다.
    const startSnapIndex = snapPoints.reduce((closestIndex, currentSnap, index) => {
      const currentDiff = Math.abs(currentSnap - startRelativeHeight);
      const closestDiff = Math.abs(snapPoints[closestIndex] - startRelativeHeight);
      return currentDiff < closestDiff ? index : closestIndex;
    }, 0);

    const dragDistance = currentRelativeHeight - startRelativeHeight; // 음수: 아래로, 양수: 위로

    // 최종 목표 스냅 인덱스를 결정합니다.
    let targetSnapIndex = startSnapIndex;
    const dragThreshold = 0.02; // 임계값을 2%로 줄임

    if (dragDistance < -dragThreshold) { // 아래로 조금만 드래그해도
      targetSnapIndex = Math.max(0, startSnapIndex - 1);
    } else if (dragDistance > dragThreshold) { // 위로 조금만 드래그해도
      targetSnapIndex = Math.min(snapPoints.length - 1, startSnapIndex + 1);
    }
    // 임계값 미만으로 움직였다면, targetSnapIndex는 startSnapIndex로 유지됩니다 (제자리).

    const finalSnapPoint = snapPoints[targetSnapIndex];
    

    snapToPoint(availableHeight * finalSnapPoint);
  }, [isDragging, currentHeight, startHeight, snapPoints, snapToPoint, availableHeight]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleMouseMove, handleDragEnd, handleTouchMove]);

  // 모든 Hook 선언 이후에 조건부 렌더링
  if (!isOpen) {
    // console.log('바텀시트 렌더링 안함: isOpen =', isOpen);
    return null;
  }

  // console.log('바텀시트 렌더링 시작:', { isOpen, currentHeight });

  return (
    <div 
      className={`bottom-sheet-container`}
      style={{ backgroundColor: 'transparent' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div
        ref={sheetRef}
        className="bottom-sheet"
        style={{ 
          backgroundColor: backgroundColor,
          height: `${currentHeight}px` // 인라인 스타일로 높이 강제 설정
        }}
      >
        {showDragHandle && (
          <div 
            className="drag-handler"
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
          >
            <div className="drag-bar"></div>
          </div>
        )}
        {header && <div className="bottom-sheet-header">{header}</div>}
        <div className="bottom-sheet-content" ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheet;